/**
 * Servicio de templates para tickets
 * Proporciona templates estructurados para diferentes tipos de tickets
 */
export const ticketTemplates = {
  /**
   * Template para el encabezado común de todos los tickets
   * @param {Object} business - Información del negocio
   * @param {Object} sale - Información de la venta
   * @returns {Object} - Objeto con secciones del encabezado
   */
  header(business, sale) {
    const formattedDate = new Date().toLocaleString();
    return {
      businessName: business?.name?.toUpperCase() || 'TIBO APP',
      saleNumber: sale.display_id || sale.id?.slice(-8),
      date: formattedDate,
    };
  },

  /**
   * Template para el pie de página común de todos los tickets
   * @returns {Object} - Objeto con secciones del pie de página
   */
  footer() {
    return {
      thankYouMessage: '¡Gracias por su compra!',
      website: 'www.tibo.app',
    };
  },

  /**
   * Template para la información del cliente
   * @param {Object} customer - Información del cliente
   * @returns {Object} - Objeto con secciones de información del cliente
   */
  customerInfo(customer) {
    if (!customer?.name) return null;
    
    return {
      name: customer.name,
      phone: customer.phone,
      address: customer.address,
    };
  },

  /**
   * Template para el ticket de entrega (delivery)
   * @param {Object} options - Opciones para generar el ticket
   * @returns {Object} - Objeto con todas las secciones del ticket
   */
  deliveryTicket(options) {
    const { business, sale, items, customer } = options;
    
    // Productos formateados
    const formattedItems = items?.map(item => ({
      name: item.title?.substring(0, 14) || 'Producto',
      quantity: item.quantity || 1,
      unitPrice: item.unit_price || 0,
      total: (item.quantity * item.unit_price) || 0,
    })) || [];
    
    // Totales
    const totals = {
      subtotal: sale.total_amount || 0,
      depositAmount: sale.total_deposit_amount || 0,
      total: (sale.total_amount + (sale.total_deposit_amount || 0)) || 0,
    };
    
    return {
      header: this.header(business, sale),
      items: formattedItems,
      totals,
      customer: this.customerInfo(customer),
      footer: this.footer(),
    };
  },

  /**
   * Template para el ticket de preparación (fulfillment)
   * @param {Object} options - Opciones para generar el ticket
   * @returns {Object} - Objeto con todas las secciones del ticket
   */
  fulfillmentTicket(options) {
    const { business, sale, items } = options;
    
    // Productos formateados (solo nombre y cantidad)
    const formattedItems = items?.map(item => ({
      name: item.title?.substring(0, 25) || 'Producto',
      quantity: item.quantity || 1,
    })) || [];
    
    return {
      header: this.header(business, sale),
      items: formattedItems,
      notes: sale.notes,
      footer: this.footer(),
    };
  },

  /**
   * Template para el ticket de respaldo (backup)
   * @param {Object} options - Opciones para generar el ticket
   * @returns {Object} - Objeto con todas las secciones del ticket
   */
  backupTicket(options) {
    const { business, sale, items, customer } = options;
    
    // Productos formateados
    const formattedItems = items?.map(item => ({
      name: item.title?.substring(0, 14) || 'Producto',
      quantity: item.quantity || 1,
      unitPrice: item.unit_price || 0,
      total: (item.quantity * item.unit_price) || 0,
    })) || [];
    
    // Totales
    const totals = {
      subtotal: sale.total_amount || 0,
      depositAmount: sale.total_deposit_amount || 0,
      total: (sale.total_amount + (sale.total_deposit_amount || 0)) || 0,
    };
    
    // Información de pago
    const payment = sale.payment_method ? {
      method: sale.payment_method,
      reference: sale.payment_reference,
    } : null;
    
    return {
      header: this.header(business, sale),
      items: formattedItems,
      totals,
      customer: this.customerInfo(customer),
      payment,
      footer: this.footer(),
    };
  },

  /**
   * Template para el ticket de prueba
   * @param {Object} business - Información del negocio
   * @returns {Object} - Objeto con todas las secciones del ticket
   */
  testTicket(business) {
    // Productos de ejemplo
    const exampleItems = [
      {
        name: 'Café Americano',
        quantity: 2,
        unitPrice: 45.00,
        total: 90.00,
      },
      {
        name: 'Croissant',
        quantity: 1,
        unitPrice: 35.00,
        total: 35.00,
      },
    ];
    
    // Totales
    const totals = {
      subtotal: 125.00,
      depositAmount: 0,
      total: 125.00,
    };
    
    return {
      title: '=== PRUEBA DE IMPRESIÓN ===',
      businessName: business?.name?.toUpperCase() || 'TIBO APP',
      businessId: business?.id,
      date: new Date().toLocaleString(),
      items: exampleItems,
      totals,
      footer: this.footer(),
    };
  },
}; 